import { type MenuContentCategoryWithSlug, type MenuContentProduct } from '../../MenuContent.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Flattens menu categories into list rows: a heading and one row per product,
 * with the last product closing the category spacing. Custom categories
 * collapse into a single row that lays out its own cards. Dense
 * categories render their products in pairs, one row per pair. Categories
 * listed for the bundle entry point open with its card, right after the heading.
 */
export function buildMenuContentListRows(params: Params): MenuContentListRow[] {
  const { categories, reorderRailTargetId, denseCategoryIds, bundleEntryPointCategoryIds } = params;

  const reorderRailRows = reorderRailTargetId ? [buildReorderRailRow(reorderRailTargetId)] : [];
  const categoryRows = categories.flatMap((category, categoryIndex) =>
    buildCategoryRows({ category, categoryIndex, denseCategoryIds, bundleEntryPointCategoryIds }),
  );

  return [...reorderRailRows, ...categoryRows];
}

export function getMenuContentListRowTargetId(row: MenuContentListRow) {
  return row.targetId;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildReorderRailRow(targetId: string): MenuContentListRow {
  return { kind: 'reorder-rail', key: targetId, targetId };
}

function buildCategoryRows(params: CategoryRowsParams): MenuContentListRow[] {
  const { category, categoryIndex, denseCategoryIds, bundleEntryPointCategoryIds } = params;

  const rowBase: CategoryRowBase = { key: category.slug, targetId: category.slug, category, categoryIndex }; // prettier-ignore

  if (category.isCustom) return buildCustomCategoryRows(rowBase);

  const hasBundleEntryPoint = Boolean(bundleEntryPointCategoryIds?.has(category.id));

  const leadingRows = [
    buildCategoryHeadingRow(rowBase),
    ...(hasBundleEntryPoint ? [buildBundleEntryPointRow(rowBase)] : []),
  ];

  if (denseCategoryIds?.has(category.id)) {
    return [...leadingRows, ...buildDenseProductRows(rowBase)];
  }

  const lastProductIndex = category.products.length - 1;
  const productRows = category.products.map<MenuContentListRow>((product, productIndex) => ({
    ...rowBase,
    kind: 'product',
    key: `${rowBase.key}:product:${product.id}`,
    product,
    isLastProduct: productIndex === lastProductIndex,
  }));

  return [...leadingRows, ...productRows];
}

function buildDenseProductRows(rowBase: CategoryRowBase): MenuContentListRow[] {
  const productPairs = chunkPairs(rowBase.category.products);
  const lastPairIndex = productPairs.length - 1;
  const pairRows = productPairs.map<MenuContentListRow>((products, pairIndex) => ({
    ...rowBase,
    kind: 'dense-product-pair',
    key: `${rowBase.key}:dense:${products[0]?.id ?? pairIndex}`,
    products,
    isLastRow: pairIndex === lastPairIndex,
  }));

  return pairRows;
}

function buildCategoryHeadingRow(rowBase: CategoryRowBase): MenuContentListRow {
  return { ...rowBase, kind: 'category-heading', key: `${rowBase.key}:heading` };
}

function buildBundleEntryPointRow(rowBase: CategoryRowBase): MenuContentListRow {
  return { ...rowBase, kind: 'bundle-entry-point', key: `${rowBase.key}:bundle-entry-point` };
}

function chunkPairs<T>(items: readonly T[]): T[][] {
  return Array.from({ length: Math.ceil(items.length / 2) }, (_, pairIndex) =>
    items.slice(pairIndex * 2, pairIndex * 2 + 2),
  );
}

function buildCustomCategoryRows(rowBase: CategoryRowBase): MenuContentListRow[] {
  const customProducts = rowBase.category.products.filter((product) => product.isCustom);

  if (customProducts.length === 0) return [];

  return [{ ...rowBase, kind: 'custom-products', key: `${rowBase.key}:custom`, products: customProducts }]; // prettier-ignore
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  categories: readonly MenuContentCategoryWithSlug[];
  reorderRailTargetId: string | undefined;
  denseCategoryIds?: Set<string>;
  bundleEntryPointCategoryIds?: Set<string>;
};

type CategoryRowsParams = Omit<Params, 'categories' | 'reorderRailTargetId'> & {
  category: MenuContentCategoryWithSlug;
  categoryIndex: number;
};

type RowBase = { key: string; targetId: string };

type CategoryRowBase = RowBase & {
  category: MenuContentCategoryWithSlug;
  categoryIndex: number;
};

export type MenuContentListRow =
  | (RowBase & { kind: 'reorder-rail' })
  | (CategoryRowBase & { kind: 'category-heading' })
  | (CategoryRowBase & { kind: 'bundle-entry-point' })
  | (CategoryRowBase & { kind: 'product'; product: MenuContentProduct; isLastProduct: boolean })
  | (CategoryRowBase & { kind: 'custom-products'; products: MenuContentProduct[] })
  | (CategoryRowBase & { kind: 'dense-product-pair'; products: MenuContentProduct[]; isLastRow: boolean }); // prettier-ignore
